import React, { useContext, useRef, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import pick from 'lodash/pick';
import { ThemeProvider } from '@alefeducation/ui-elements-library-core';
import FeatureFlags from '../../featureToggles/FeatureFlags';
import { useLanguage } from '../../utils/language';
import InputModal from '../common/InputModal/InputModal';
import submissionResponse from '../../apollo/mutations/submissionResponse.graphql';
import submissionsReturn from '../../apollo/mutations/submissionsReturn.graphql';
import { buildSubmissionsReturnInput } from '../../helpers/buildSubmissionsReturnInput';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import fireNotification from '../../utils/fireNotification';
import { afterSubmissionResponseReturn } from '../../helpers/afterSubmissionResponseReturn';
import handleGraphQLMutationErrors from '../../helpers/handleGraphQLMutationErrors';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import { buildSubmissionListFromGroupedSubmissions } from '../../helpers/buildSubmissionListFromGroupedSubmissions';
import FeedbackTextArea from '../common/FeedbackTextArea/FeedbackTextArea';
import assignmentTypes from '../../utils/assignmentTypes';
import { StudentSubmissionProvider } from '../AssignmentSubmissionEvaluate/StudentSubmissionPageContext';
import { classAssignmentListQuery } from '../AssignmentSubmissionEvaluate/constants';
import useLDClientWrapper from '../../featureToggles/useLDClientWrapper';
import Styles from './studentListStyles';
import { AssignmentStudentListProps } from './helpers';
import { NewAssignmentStudentsList } from './UpdatedClassDashboard/NewAssignmentStudentsList/NewAssignmentStudentList';
import { StudentListContext } from './UpdatedClassDashboard/StudentListContext';

const AssignmentStudentsList = ({
  groupedSubmissions,
  unAssignedStudents,
  dueOn,
  maxScore,
  maxStars,
  gradeable,
  attachmentRequired,
  commentRequired,
  onNavigateToStudent,
  onSubmissionsReturn,
  teamsData,
}) => {
  const { t } = useTranslation();
  const ldClient = useLDClientWrapper();
  const {
    assignmentParams: {
      assignmentInstanceId,
      classId,
      mloId,
      contentId,
      instructionalPlanId,
      academicYearId,
      finishExperienceContext,
    },
    assignmentData,
  } = useContext(AssignmentContext);
  const { langCode } = useLanguage();

  const isLockingOfAssignmentEnabled =
    assignmentData.assignmentType === assignmentTypes.ALEF
      ? ldClient.variation(FeatureFlags.ALEF_ASSIGNMENT_ASSIGN, true)
      : ldClient.variation(FeatureFlags.TEACHER_ASSIGNMENT_ASSIGN, true);

  const isStudentsWhoLeftTheClassEnabled =
    assignmentData.assignmentType === assignmentTypes.TEACHER;

  const [feedbackModalDetails, setFeedbackModalDetails] = useState({
    isOpen: false,
    studentSubmission: null,
  });
  const [
    submissionsReturnModalDetails,
    setSubmissionsReturnModalDetails,
  ] = useState({
    isOpen: false,
    studentName: null,
    isReturnIndividual: false,
  });
  const [isFailedLockModalOpen, setIsFailedLockModalOpen] = useState(false);
  const [
    refetchingClassAssignmentListInProgress,
    setRefetchingClassAssignmentListInProgress,
  ] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const commentInputElm = useRef(null);
  const [focusedGradeInputStudentId, setFocusedGradeInputStudentId] = useState(
    null,
  );
  const [submissionResponseCommentMutation] = useMutation(submissionResponse, {
    onError: error =>
      handleGraphQLMutationErrors(error, 'NetworkError', langCode),
  });
  const submissionReturnMutationOptions = {
    onError: error =>
      handleGraphQLMutationErrors(error, 'NetworkError', langCode),
    onCompleted: data => {
      onSubmissionsReturn(data.submissionsReturn);
      fireNotification('TeacherSubmissionReturnSuccess', langCode);
    },
    refetchQueries: [classAssignmentListQuery],
  };
  const [submissionsReturnMutation] = useMutation(
    submissionsReturn,
    submissionReturnMutationOptions,
  );
  const [
    studentSubmissionIdsToReturn,
    setStudentSubmissionIdsToReturn,
  ] = useState([]);

  const inputModalCloseCleanup = () => {
    setFeedbackModalDetails({ isOpen: false });
    setFeedbackComment('');
  };

  const confirmModalCloseCleanup = () => {
    setStudentSubmissionIdsToReturn([]);
    setSubmissionsReturnModalDetails({
      isOpen: false,
      studentName: null,
      isReturnIndividual: false,
    });
  };

  const handleFeedbackClick = React.useCallback((submission, student) => {
    setFeedbackComment(
      (submission && submission.response && submission.response.comment) || '',
    );

    setFeedbackModalDetails({
      isOpen: true,
      studentSubmission: submission,
      student,
    });
  }, []);

  const handleGradeScoreFocus = studentId => {
    setFocusedGradeInputStudentId(studentId);
  };
  const handleGradeScoreBlur = () => {
    setFocusedGradeInputStudentId(null);
  };

  const onViewStudent = React.useCallback(
    student =>
      onNavigateToStudent({
        assignmentInstanceId,
        assignmentId: assignmentData.assignmentId,
        classId,
        contentId,
        academicYearId,
        mloId,
        studentId: student.id,
      }),
    [
      (assignmentInstanceId,
      assignmentData.assignmentId,
      classId,
      contentId,
      mloId),
    ],
  );

  return (
    <ThemeProvider>
      <Styles.Wrapper>
        {feedbackModalDetails.isOpen && (
          <InputModal
            lang={langCode}
            onClose={() => inputModalCloseCleanup()}
            onConfirm={async () => {
              await submissionResponseCommentMutation({
                variables: {
                  id: feedbackModalDetails.studentSubmission
                    ? feedbackModalDetails.studentSubmission.id
                    : undefined,
                  submissionTeacherInput: !feedbackModalDetails.studentSubmission
                    ? {
                        assignmentId: assignmentData.assignmentId,
                        referrerId: assignmentInstanceId || mloId,
                        studentId: feedbackModalDetails.student.id,
                        mloId,
                        classId,
                        instructionalPlanId,
                        contentId,
                      }
                    : undefined,
                  input: {
                    comment: feedbackComment || null,
                    score:
                      feedbackModalDetails.studentSubmission &&
                      feedbackModalDetails.studentSubmission.response
                        ? feedbackModalDetails.studentSubmission.response.score
                        : null,
                    attachment:
                      feedbackModalDetails.studentSubmission &&
                      feedbackModalDetails.studentSubmission.response &&
                      feedbackModalDetails.studentSubmission.response.attachment
                        ? pick(
                            feedbackModalDetails.studentSubmission.response
                              .attachment,
                            ['fileId', 'fileName', 'path'],
                          )
                        : null,
                  },
                },
                update: afterSubmissionResponseReturn({
                  isReturning: false,
                  attachmentRequired,
                  commentRequired,
                }),
              });
              inputModalCloseCleanup();

              if (!gradeable) {
                if (feedbackComment)
                  fireNotification(
                    'evaluatedStudentByName',
                    langCode,
                    t('{{STUDENT_NAME}} assignment has been evaluated', {
                      STUDENT_NAME: feedbackModalDetails.student.name,
                    }),
                  );
                else
                  fireNotification(
                    'unEvaluatedStudentByName',
                    langCode,
                    t(
                      '{{STUDENT_NAME}} assignment evaluation has been removed',
                      {
                        STUDENT_NAME: feedbackModalDetails.student.name,
                      },
                    ),
                  );
              }
            }}
            title={t('Feedback')}
            shouldConfirm={
              !!feedbackModalDetails.studentSubmission &&
              !!feedbackModalDetails.studentSubmission.returnedOn
            }
          >
            <Styles.TextAreaWrapper>
              <FeedbackTextArea
                data-testid="feedback-comment"
                className="input-modal__textarea usprivacy"
                placeholder={t('Put your text here')}
                ref={commentInputElm}
                onChange={() => {
                  setFeedbackComment(commentInputElm.current.value);
                }}
                defaultValue={
                  feedbackModalDetails.studentSubmission &&
                  feedbackModalDetails.studentSubmission.response &&
                  feedbackModalDetails.studentSubmission.response.comment
                }
                readOnly={
                  !!feedbackModalDetails.studentSubmission &&
                  !!feedbackModalDetails.studentSubmission.returnedOn
                }
              />
            </Styles.TextAreaWrapper>
          </InputModal>
        )}
        {submissionsReturnModalDetails.isOpen ? (
          <ConfirmationModal
            lang={langCode}
            loading={false}
            onClose={confirmModalCloseCleanup}
            onConfirm={async () => {
              await submissionsReturnMutation({
                variables: {
                  input: buildSubmissionsReturnInput(
                    studentSubmissionIdsToReturn,
                    finishExperienceContext,
                  ),
                },
                update: afterSubmissionResponseReturn({
                  isReturning: true,
                  commentRequired,
                  attachmentRequired,
                }),
              });

              if (submissionsReturnModalDetails.isReturnIndividual)
                fireNotification(
                  'returnedStudentsByName',
                  langCode,
                  t('{{STUDENT_NAME}} assignment has been Returned', {
                    STUDENT_NAME: submissionsReturnModalDetails.studentName,
                  }),
                );
              else
                fireNotification(
                  'returnedStudentsByCount',
                  langCode,
                  t(
                    studentSubmissionIdsToReturn.length === 1
                      ? '{{ASSIGNMENT_COUNT}} assignment have been returned'
                      : '{{ASSIGNMENT_COUNT}} assignments have been returned',
                    {
                      ASSIGNMENT_COUNT: studentSubmissionIdsToReturn.length,
                    },
                  ),
                );

              confirmModalCloseCleanup();
            }}
            title={t('Submission Return')}
          >
            <span>
              {t(
                'Are you sure you want to return the grade/feedback to the student(s)?',
              )}
            </span>
          </ConfirmationModal>
        ) : null}
        <StudentListContext.Provider
          value={{
            isLockingOfAssignmentEnabled,
            isStudentsWhoLeftTheClassEnabled,
            submissionsCount: buildSubmissionListFromGroupedSubmissions(
              groupedSubmissions,
            ).length,
            groupedSubmissions,
            unAssignedStudents,
            maxScore,
            maxStars,
            dueOn,
            attachmentRequired,
            commentRequired,
            gradeable,
            onAddFeedbackClick: handleFeedbackClick,
            onFeedbackClick: handleFeedbackClick,
            onGradeScoreFocus: handleGradeScoreFocus,
            onGradeScoreBlur: handleGradeScoreBlur,
            focusedGradeInputStudentId,
            onViewStudent,
            setSubmissionsReturnModalDetails,
            setStudentSubmissionIdsToReturn,
            teamsData,
            isFailedLockModalOpen,
            setIsFailedLockModalOpen,
            refetchingClassAssignmentListInProgress,
            setRefetchingClassAssignmentListInProgress,
          }}
        >
          <StudentSubmissionProvider>
            <NewAssignmentStudentsList />
          </StudentSubmissionProvider>
        </StudentListContext.Provider>
      </Styles.Wrapper>
    </ThemeProvider>
  );
};

AssignmentStudentsList.defaultProps = {
  maxScore: null,
  maxStars: null,
  onNavigateToStudent: () => {},
  onSubmissionsReturn: () => {},
};
AssignmentStudentsList.propTypes = AssignmentStudentListProps;
export default AssignmentStudentsList;
